import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, ReplaySubject, map, switchMap } from "rxjs";
import { LanguageApiService, LanguageModel } from "../services/api-services";
import LanguageCodes from "../constants/language-codes";

@Injectable()
export class LanguageService {
  public static readonly defaultLanguageCode: string = LanguageCodes.german;
  private readonly currentLanguageCodelocalStorageKey = 'Claims.current-language-code';
  private readonly languages$ = new ReplaySubject<LanguageModel[]>(1);
  private readonly currentLanguage = new ReplaySubject<LanguageModel>(1);
  public readonly currentLanguage$ = this.currentLanguage.asObservable();

  constructor(
    private readonly translateService: TranslateService,
    private readonly languageApiService: LanguageApiService
  ) { }

  init(): Observable<any> {
    return this.languageApiService.get(null)
      .pipe(switchMap(languages => {
        this.languages$.next(languages);
        this.languages$.complete();

        return this.detectLanguage()
          .pipe(switchMap(languageCode => this.setLanguageByCode(languageCode)));
      }));
  }

  public getLanguages(): Observable<LanguageModel[]> {
    return this.languages$;
  }

  private detectLanguage(): Observable<string> {
    return this.languages$.pipe(map(languages => {
      // Try to use language from local storage first
      let currentLanguageCode: string | null = localStorage.getItem(this.currentLanguageCodelocalStorageKey);

      // Then try to use language from browser
      if (!currentLanguageCode && navigator.language) {
        const browserLanguagePrefix = navigator.language.substring(0, 2);

        currentLanguageCode = this.findLanguageCodeByTwoLetterLanguageCode(languages, browserLanguagePrefix);
      }

      // Otherwise use default language code
      if (!currentLanguageCode) {
        currentLanguageCode = LanguageService.defaultLanguageCode;
      }

      return currentLanguageCode;
    }));
  }

  public setLanguageByCode(code: string): Observable<LanguageModel> {
    return this.languages$.pipe(switchMap(languages => {
      const language = languages.find(language => language.code == code)!;

      return this.setLanguage(language)
    }));
  }

  public setLanguage(language: LanguageModel): Observable<LanguageModel> {
    return this.translateService.use(language.code)
      .pipe(map(() => {
        localStorage.setItem(this.currentLanguageCodelocalStorageKey, language.code);
        this.currentLanguage.next(language);

        return language;
      }));
  }

  private findLanguageCodeByTwoLetterLanguageCode(languages: LanguageModel[], prefix: string): string {
    return languages.find(language => language.code.substring(0, 2) === prefix)?.code ?? LanguageService.defaultLanguageCode;
  }

  get currentLanguageCode(): string {
    return this.translateService.currentLang;
  }
}
